import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Search } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PermissionCategory } from './PermissionCategory';
const EMPTY_INHERITED = [];
export function PermissionEditor({ enabledPermissions, onChange, categories, dependencies, inheritedPermissions = EMPTY_INHERITED, disabled = false, className, }) {
    const { t } = useTranslation('settings');
    const [searchQuery, setSearchQuery] = useState('');
    const enabledSet = useMemo(() => new Set(enabledPermissions), [enabledPermissions]);
    const inheritedSet = useMemo(() => new Set(inheritedPermissions), [inheritedPermissions]);
    // Build dependency map for quick lookup
    const dependencyMap = useMemo(() => {
        const map = new Map();
        dependencies.forEach((dep) => {
            const current = map.get(dep.permissionKey) || [];
            current.push(dep.dependsOnKey);
            map.set(dep.permissionKey, current);
        });
        return map;
    }, [dependencies]);
    // Flatten all permissions with their dependencies
    const allPermissions = useMemo(() => {
        return categories.flatMap((cat) => cat.permissions.map((p) => ({
            key: p.key,
            label: p.label,
            tooltip: p.tooltip,
            categoryKey: cat.key,
            dependencies: dependencyMap.get(p.key) || [],
        })));
    }, [categories, dependencyMap]);
    // Calculate overridden permissions (different from inherited)
    const overriddenSet = useMemo(() => {
        const overridden = new Set();
        enabledPermissions.forEach((p) => {
            if (!inheritedSet.has(p)) {
                overridden.add(p);
            }
        });
        inheritedPermissions.forEach((p) => {
            if (!enabledSet.has(p)) {
                overridden.add(p);
            }
        });
        return overridden;
    }, [enabledPermissions, inheritedPermissions, enabledSet, inheritedSet]);
    // Filter permissions by search
    const filteredPermissions = useMemo(() => {
        if (!searchQuery.trim()) {
            return allPermissions;
        }
        const query = searchQuery.toLowerCase();
        return allPermissions.filter((p) => p.label.toLowerCase().includes(query) ||
            (p.tooltip?.toLowerCase().includes(query) ?? false) ||
            p.key.toLowerCase().includes(query));
    }, [searchQuery, allPermissions]);
    // Group by category
    const permissionsByCategory = useMemo(() => {
        const grouped = new Map();
        categories.forEach((cat) => {
            grouped.set(cat.key, []);
        });
        filteredPermissions.forEach((p) => {
            const current = grouped.get(p.categoryKey) || [];
            current.push(p);
            grouped.set(p.categoryKey, current);
        });
        return grouped;
    }, [filteredPermissions, categories]);
    // Handle permission change - simple toggle without dependency cascading
    const handlePermissionChange = useCallback((permissionKey, enabled) => {
        const newPermissions = new Set(enabledPermissions);
        if (enabled) {
            newPermissions.add(permissionKey);
        }
        else {
            newPermissions.delete(permissionKey);
        }
        onChange(Array.from(newPermissions));
    }, [enabledPermissions, onChange]);
    // Handle bulk permission changes (select all / deselect all for a category)
    const handleBulkPermissionChange = useCallback((permissionKeys, enabled) => {
        const newPermissions = new Set(enabledPermissions);
        if (enabled) {
            // Enable all specified permissions and their dependencies
            permissionKeys.forEach((permissionKey) => {
                newPermissions.add(permissionKey);
                const addDependencies = (pKey) => {
                    const deps = dependencyMap.get(pKey) || [];
                    deps.forEach((dep) => {
                        if (!newPermissions.has(dep)) {
                            newPermissions.add(dep);
                            addDependencies(dep);
                        }
                    });
                };
                addDependencies(permissionKey);
            });
        }
        else {
            // Disable all specified permissions and permissions that depend on them
            permissionKeys.forEach((permissionKey) => {
                newPermissions.delete(permissionKey);
                const removeDependents = (pKey) => {
                    allPermissions.forEach((perm) => {
                        if (perm.dependencies.includes(pKey) && newPermissions.has(perm.key)) {
                            newPermissions.delete(perm.key);
                            removeDependents(perm.key);
                        }
                    });
                };
                removeDependents(permissionKey);
            });
        }
        onChange(Array.from(newPermissions));
    }, [enabledPermissions, onChange, allPermissions, dependencyMap]);
    const enabledCount = enabledPermissions.length;
    const totalCount = allPermissions.length;
    return (_jsxs("div", { className: className, children: [_jsxs("div", { className: "mb-4 flex items-center justify-between gap-4", children: [_jsxs("div", { className: "relative flex-1", children: [_jsx(Search, { className: "text-muted-foreground absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2" }), _jsx(Input, { placeholder: t('users.permissions.searchPlaceholder'), value: searchQuery, onChange: (e) => setSearchQuery(e.target.value), className: "pl-9" })] }), _jsx("span", { className: "text-muted-foreground text-sm", children: t('users.permissions.enabledCount', { enabled: enabledCount, total: totalCount }) })] }), _jsx(ScrollArea, { className: "h-[400px] pr-4", children: _jsx("div", { className: "space-y-4", children: categories.map((category) => {
                        const categoryPermissions = permissionsByCategory.get(category.key) || [];
                        if (categoryPermissions.length === 0)
                            return null;
                        const categoryInfo = {
                            key: category.key,
                            label: category.label,
                            description: category.description || '',
                        };
                        const permissions = categoryPermissions.map((p) => ({
                            key: p.key,
                            label: p.label,
                            tooltip: p.tooltip || '',
                            category: p.categoryKey,
                        }));
                        return (_jsx(PermissionCategory, { category: categoryInfo, permissions: permissions, enabledPermissions: enabledSet, onPermissionChange: handlePermissionChange, onBulkPermissionChange: handleBulkPermissionChange, inheritedPermissions: inheritedSet, overriddenPermissions: overriddenSet, disabled: disabled }, category.key));
                    }) }) })] }));
}
